import React, { ReactNode, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import Tabs, { TabPane } from 'rc-tabs';
import { DateTime } from 'luxon';
import { renderToString } from 'react-dom/server';
import { Spinner } from 'reactstrap';
import { useAxios } from '../../../../../../../../../../shared/hooks/useAxios';
import Modal from '../../../../../../../../../../shared/design-system/components/atoms/modal';
import { ActivityType } from '../../../../../../../../../../shared/design-system/components/organisms/activity/types';
import { getOffset } from '../../../../../../../../../../shared/utils/time-conversions';
import ShClockIcon from '../../../../../../../../../../shared/components/images/sh-clock';
import { ProspectActivityTabs } from '../../../../../../../../enums';
import Tab from '../../../../../../../../../../shared/design-system/components/atoms/tab';
import { getEngagementTabIcon } from './helpers/get-tab-icon';
import ActivityComponent from '../../../../../../../../../../shared/design-system/components/molecules/activity-component';
import EmptyDataIcon from '../../../../../../../../../../shared/components/images/empty-data-icon-1';
import toaster from '../../../../../../../../../../shared/toaster';
import {
  checkIsActivityExsist,
  getActivityData,
  getActivityWithLabel,
  getLabel,
  handleActivityItemsFilters,
} from './helpers';
import MasterLog from '../../../../../../../../../../shared/enums/master-log-enum';
import Icon from '../../../../../../../../../../shared/design-system/components/atoms/icon';

type ProspectActivityProps = {
  timezone: string;
  prospectFullName: string;
  prospectEmail: string;
  sequenceId: string;
  prospectId: string;
  type: string;
  show: boolean;
  onHide: () => void;
  t: (x: string) => ReactNode;
  isSubsequence?: boolean;
};

type ActivityTileProps = {
  log: {
    step: {
      number: string;
    };
    sequence: {
      name: string;
    };
    subSequence: {
      name: string;
    };
    link: {
      link: string;
    };
  };
  type: MasterLog;
  timezone: string;
  timestamp: string;
  t: (x: string) => ReactNode;
  isSubsequence: boolean;
};

const tabs = [
  ProspectActivityTabs.All,
  ProspectActivityTabs.Sent,
  ProspectActivityTabs.Opened,
  ProspectActivityTabs.Clicked,
  ProspectActivityTabs.Replied,
];

const ActivityTile: React.FC<ActivityTileProps> = ({
  timestamp,
  log,
  type,
  timezone,
  t,
  isSubsequence,
}: ActivityTileProps) => {
  const time = DateTime.fromISO(timestamp, { zone: timezone }).toFormat(
    'hh:mm a',
  );

  let activityName = '';

  switch (type) {
    case MasterLog.ReplyReceivedManual:
    case MasterLog.ReplyReceivedAuto:
      activityName = 'replied';
      break;
    case MasterLog.EmailSent:
      activityName = 'sent';
      break;
    case MasterLog.EmailOpened:
      activityName = 'opened';
      break;
    case MasterLog.LinkClicked:
      activityName = 'link_clicked';
      break;
    default:
      break;
  }

  return (
    <div className="prospect-activity-tile">
      <div className="prospect-activity-content">
        <strong className="prospect-current-step">
          Step {log.step.number}{' '}
          {!isSubsequence &&
            ` (${log.subSequence?.name ?? log.sequence?.name})`}
        </strong>{' '}
        <div className="prospect-activity-name">
          {t('labels.email')} {t(`labels.${activityName}`)}
        </div>
        <div className="prospect-activity-time">
          <ShClockIcon />{' '}
          <span>
            {time} (GMT {getOffset(timestamp, timezone)}) {timezone}{' '}
          </span>
        </div>
      </div>
      {/* renderToString() converts react components into a string, and no event handlers or lifecycle events could be applied to those components. Decided to implement it with the help of the CSS tooltip. */}
      {log.link && type === MasterLog.LinkClicked ? (
        <div className="tooltip-wrapper">
          <div className="prospect-link-clicked">
            <>
              <a target="_blank" rel="noopener noreferrer" href={log.link.link}>
                {log.link.link}
              </a>
              <span className="tooltiptext">{log.link.link}</span>
            </>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ProspectActivityModal: React.FC<ProspectActivityProps> = ({
  prospectFullName,
  prospectEmail,
  timezone,
  sequenceId,
  prospectId,
  type,
  show,
  onHide,
  t,
  isSubsequence,
}) => {
  const [activeTab, setActiveTab] = useState(type);

  const [activityItems, setActivityItems] = useState(null);
  const { loading, response } = useAxios(
    {
      url: `/activity/prospect/${prospectId}/sequence/${sequenceId}`,
      params: {
        type: [
          MasterLog.EmailSent,
          MasterLog.EmailOpened,
          MasterLog.LinkClicked,
          MasterLog.ReplyReceivedAuto,
          MasterLog.ReplyReceivedManual,
        ],
      },
    },
    [],
  );

  useEffect(() => {
    setActivityItems(getActivityData(activityItems, response));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  if (checkIsActivityExsist(loading, activityItems)) {
    toaster.error(t('messages.data_not_updated_for_selection'));
    onHide();
    return null;
  }

  const generatedTabs = tabs.map((tab) => {
    // Creating an activity arr
    let activityArr: ActivityType[] = [];

    // Set to track the dayText that we already have got. like "20 July 2020"
    const dayTextSet = new Set();

    activityItems
      ?.filter((item) => handleActivityItemsFilters(tab, item))
      .forEach(({ timestamp, log, key }) => {
        // Generate Log HTML
        const logHtml = renderToString(
          <ActivityTile
            timestamp={timestamp}
            log={log}
            type={key}
            timezone={timezone}
            t={t}
            isSubsequence={isSubsequence}
          />,
        );

        const ts = DateTime.fromISO(timestamp, { zone: timezone })
          .toFormat('dd LLLL yyyy')
          .toString();

        if (!dayTextSet.has(ts)) {
          const activityArrElement: ActivityType = {
            dayText: ts,
            activity: [{ log: logHtml, key }],
          };
          dayTextSet.add(ts);
          activityArr.push(activityArrElement);
        } else {
          const activityArrElement = activityArr.find(
            ({ dayText }) => dayText === ts,
          );
          activityArrElement.activity.push({ log: logHtml, key });
        }
      });

    activityArr = getActivityWithLabel(activityArr, t);

    const activityCount = activityArr.reduce(
      (a, c) => a + c.activity.length,
      0,
    );

    return (
      <TabPane
        tab={
          <Tab
            label={t(`labels.${getLabel(tab)}`)}
            icon={<Icon identifier={getEngagementTabIcon(tab)} />}
            counter={activityCount}
          />
        }
        key={tab}
      >
        {loading && activeTab === tab && (
          <div className="text-center mt-5">
            <Spinner />
          </div>
        )}
        
        {!loading &&
          activityArr.length > 0 &&
          activityArr.map(({ id, dayText, activity }) => (
            <ActivityComponent key={id} dayText={dayText} activity={activity} />
          ))}

        {!loading && activityArr.length === 0 && (
          <div className="empty-block">
            <div className="empty-icon-wrapper">
              <EmptyDataIcon />
            </div>
            <div className="empty-message-wrapper">
              <h2 className="empty-message-heading">
                {t('labels.no_data_available')}
              </h2>
              <div className="empty-message">
                <p className="description">{t('messages.no_data_available')}</p>
              </div>
            </div>
          </div>
        )}
      </TabPane>
    );
  });

  return (
    <Modal
      show={show}
      className="prospect-activity-container"
      titleContent={
        <div className="header-container">
          <span className="prospect-fullname-header">
            {prospectFullName.trim().length > 0 ? `${prospectFullName} - ` : ''}
            All Steps
          </span>
          <br />
          <span className="prospect-email-header">{prospectEmail}</span>
        </div>
      }
      onClose={onHide}
      showCloseIcon={true}
      hideFooter={true}
      backdrop="static"
      extraModalProps={{
        centered: true,
        'aria-labelledby': 'contained-modal-title-vcenter',
      }}
    >
      <div className="prospect-tab-action">
        <Tabs
          activeKey={activeTab}
          defaultActiveKey="all"
          prefixCls="bs-tabs"
          className="bs-tabs-small"
          onChange={(key) => {
            setActiveTab(key);
          }}
        >
          {generatedTabs}
        </Tabs>
      </div>
    </Modal>
  );
};

export default withTranslation()(ProspectActivityModal);
